import WDElement from '../wd-element.js';
import WDUtil from '../../core/wd-util';
import WDModelItems from '../wd-model-items';
import WDModelSortItems from '../wd-model-sort-items.js';
import { WDListItem, WDListHeader, WDListFooter } from './wd-list-item.js';

export default class WDList extends WDElement
{
    #wrapper = null;
    #body = null;
    #header = null;
    #footer = null;
    #selectedIndex = 0;
    #noResults = null;
    #loading = null;
    #sortable = null;

    #config = {
        rows: {
            columns: [],
            icon: false,
            checkbox: false,
            buttons: [],
            disclosure: false,
            focusable: false,
            sortable: false,
            valueField: 'value',
            drawerHandler: null,
            contentHandler: null,
        },
        model: {
            data: [],
            fetch: null,
            initFetch: false,
            method: 'POST',
            allowSort: true,
            sortField: null,
            sortDir: 'asc'
        },
        showHeader: true,
        showLoading: false,
        loadingIcon: 'fa-solid fa-spinner',
        responsive: undefined,
        fillHeight: false,
        focusable: false,
        selectAll: false,       //select-all checkbox in the header (implies rows.checkbox)
    };

    suspendEvents = false;
    suspendAnimations = false;

    constructor(cfg) 
    {
        super();
        this.classList.add('wd-list');

        WDUtil.merge(this.#config, cfg);

        //a select-all header only makes sense with row checkboxes
        if (this.#config.selectAll) this.#config.rows.checkbox = true;

        //build the base structure
        this.#wrapper = wdc('<div class="wd-list-body-wrapper"/>');

        const modelCfg = this.#config.model;
        modelCfg.createItemHandler = (data) => this.createListItem(data);
        //modelCfg.updateItemHandler = (data) => this.updateListItem(data);
        
        // Create the body as a WDProxySortItems instance
        if (cfg?.model?.sortField)
        {
            this.#body = new WDModelSortItems(modelCfg);
        }
        else
        {
            this.#body = new WDModelItems(modelCfg);
        }
       
        this.#body.classList.add('wd-list-body');
        this.#body.on('items:updated',() => this.checkResults());

        //assemble the wrapper and body - required
        this.append(this.#wrapper);
        this.#wrapper.append(this.#body);

        this.#loading = wdc('<div class="wd-list-loading">Loading...</div>');
        this.#noResults = wdc('<div class="wd-list-no-results">No Results Found</div>');

        this.#wrapper.append(this.#loading);
        this.#wrapper.append(this.#noResults);

        //make the list responsive if set
        if (cfg?.responsive !== undefined) this.responsive = cfg.responsive;
        if (cfg?.fillHeight === true) this.classList.add('is-scrollable');
        if (cfg?.focusable) this.focusable = true;

        //if no sort field set, pull one from the columns
        if (!this.sortField && this.sortColumns.length > 0)
        {
            this.sortField = this.sortColumns[0].value;
        }

        if (this.config?.showHeader) this.header = true;

        const fwdEvents = [
            'model:fetch','model:fetched','model:set',
            'item:added','sort:updated','items:added',
            'items:updated','item:updated'
        ];

        this.#body.on(fwdEvents, evt => {
            this.trigger(evt.type, evt.detail);
        } );

        this.#body.on('model:fetch',() => this.classList.add('is-loading'));
        this.#body.on('model:fetched',() => this.classList.remove('is-loading'));
       
        if (this.#config.rows?.sortable) this.setupSortable();
        return this;
    };

    checkResults()
    {
        if (this.#body.model.length > 0) this.classList.add('has-results');
        else this.classList.remove('has-results');
    }

    //public accessors	
    get config() { return this.#config; };
    get body() { return this.#body; };
    get noResults() { return this.#noResults; };
    get wrapper() { return this.#wrapper; };
    get items() { return this.#body.items; };
    get itemArray() { return this.#body.itemArray; };
    get firstItem() { return this.#body.firstItem; };
    get lastItem() { return this.#body.lastItem; };

    // Proxy model-related properties to the body
    get model() { return this.#body.model; };
    set model(data) { this.#body.model = data; };

    fetchModel() { return this.#body.fetchModel(); };
    get fetch() { return this.#body.fetch; };
    set fetch(f) { this.#body.fetch = f; };

    get filters() { return this.#body.filters; };
    set filters(f) { this.#body.filters = f; };

    get sortField() { return this.#body.sortField; };
    set sortField(field) { this.#body.sortField = field; };

    get sortDir() { return this.#body.sortDir; };
    set sortDir(dir) { this.#body.sortDir = dir; };

    get focusable() { return this.#config.focusable; };
    set focusable(val) { 
        this.#config.focusable = false; 
        if (val === true) this.addFocusEvents();
    };

    get valueField() { return this.#config.rows.valueField; };
    set valueField(vf) { this.#config.rows.valueField = vf; };

    get sortColumns()
    {
        if (this.config?.rows?.columns)
        {
            return this.config.rows.columns.filter(c => (c.sort == true));
        }
        else
        {
            return [];
        }
       
    };

    set responsive(mode)
    {
        if (mode === false) this.classList.remove('is-responsive');
        else this.classList.add('is-responsive');
    };

    // Forward methods to the body
    setSort(sortField, sortDir)
    {
        this.#body.setSort(sortField, sortDir);
    }

    filter(searchString)
    {
        return this.#body.filter(searchString);
    }

    // Item creation - can be overridden or handled via config
    createListItem(data)
    {
        // Default list item creatio
        const item = new WDListItem(this.config.rows);
        item.model = data;
        item.on('item:selected', evt => {
            this.trigger('item:selected', evt.detail);
        });

        //let the list (and its header) know about row check changes
        item.on('item:checked item:unchecked', evt => {
            this.trigger(evt.type, evt.detail);
        });
 
        return item;
    }

    updateListItem(item,data)
    {

        console.log('WDList Update List Item',item,data);
    }

    //header management
    get header()
    {
        if (!this.#header)
        {
            const headerCfg = Object.assign({}, this.config.rows, { selectAll: this.config.selectAll });
            this.#header = new WDListHeader(headerCfg);
            this.#header.classList.add('wd-list-header');
            this.insertBefore(this.#header, this.#wrapper);

            // Listen for sort requests from the header
            this.#header.on('sort:requested', (evt) =>
            {
               this.setSort(evt.detail.field, evt.detail.direction);
            });
        }

        return this.#header;
    };

    set header(val)
    {
        if (val === true) 
        {
            this.header;
        }
        else if (this.#header)
        {
            this.#header.remove();
            this.#header = null;
        }
    }

    get footer()
    {
        if (!this.#footer)
        {
            this.#footer = new WDListFooter();
            this.#footer.classList.add('wd-list-footer');
            this.append(this.#footer);
        }

        return this.#footer;
    };

    // Clear all items
    reset()
    {
        this.model = [];
    }

    get selectedItem()
    {
        return this.body.querySelector('.is-selected');
    }

    /** selects a item based on the passed value **/
    selectFromVal(val,params)
    {
        const opts = {
            suspendAnimations: false,
            suspendEvents: false,
        };

        if (params) Object.assign(opts,params);

        const item = this.itemFromVal(val);
        if (item) 
        {
            if (opts.suspendEvents) this.suspendEvents = true;
            if (opts.suspendAnimations) this.suspendAnimations = true;

            item.selected = true;

            if (opts.suspendEvents) this.suspendEvents = false;
            if (opts.suspendAnimations) this.suspendAnimations = false;
        }
    };

    deselectAll(fromItem)
    {
        const selItem = this.selectedItem;
        if (selItem && selItem != fromItem) selItem.selected = false;
    };

    /** selects a item based on the passed value **/
    itemFromVal(val)
    {
        return this.itemArray.find(x => x.value == val);
    };

    addFocusEvents()
    {
        this.tabIndex = '0';
        this.#selectedIndex = '0';

        //focus on the first item when the list is focused
        this.on('item:selected', (evt) =>
        {
            this.#selectedIndex = evt.detail.index;
        })

        this.on('results:fetched', () => this.#selectedIndex = '0');

        this.on('focusout', (evt) =>
        {
            // Only reset if focus is moving completely outside the list
            // relatedTarget is the element receiving focus
            if (!evt.relatedTarget || !this.contains(evt.relatedTarget))
            {
                this.#selectedIndex = 0;
            }
        })

        // Keyboard navigation
        this.addEventListener('keydown', (e) =>
        {
            if (e.key === 'ArrowDown')
            {
                // Stop at the end instead of wrapping
                if (this.#selectedIndex < this.items.length - 1)
                {
                    this.#selectedIndex = +this.#selectedIndex + 1;
                    this.items[this.#selectedIndex].focus();
                }
                else
                {
                    this.trigger('list:nav-escape', { direction: 'down' });
                }

                e.preventDefault();
            }
            else if (e.key === 'ArrowUp')
            {
                // Stop at the beginning instead of wrapping
                if (this.#selectedIndex > 0)
                {
                    this.#selectedIndex = this.#selectedIndex - 1;
                    this.items[this.#selectedIndex].focus();
                }
                else
                {
                    this.trigger('list:nav-escape', { direction: 'up' });
                }
                e.preventDefault();
            }
            else if (e.key === 'Tab')
            {
                if (e.shiftKey)
                {
                    this.trigger('list:nav-escape', { direction: 'up' });
                }
                else
                {
                    this.trigger('list:nav-escape', { direction: 'down' });
                }

                e.preventDefault();
            }

        }, { signal: this.abortSignal });

    };

    cleanupCallback() {
        // Call parent cleanup FIRST
        super.cleanupCallback();
        
        // Clean up the body (WDModelSortItems)
        if (this.#body) {
            this.#body.remove();
            this.#body = null;
        }
        
        // Clean up header
        if (this.#header) {
            this.#header.remove();
            this.#header = null;
        }
        
        // Clean up footer
        if (this.#footer) {
            this.#footer.remove();
            this.#footer = null;
        }
        
        // Clean up wrapper
        if (this.#wrapper) {
            this.#wrapper.remove();
            this.#wrapper = null;
        }

        // Clean up wrapper
        if (this.#noResults)
        {
            this.#noResults.remove();
            this.#noResults = null;
        }
        
        // Clear config references
        this.#config = null;
        this.#selectedIndex = 0;
    }

    setupSortable()
    {
        console.log('setup sortable');
        import('sortable').then(module =>
        {
            this.#sortable = module.Sortable.create(this.body, {
                draggable: '.wd-list-item',
                handle: '.fa-sort',
                animation: 150,
                swapThreshold: 0.65,
                onStart: (evt) => this.handleSortBegin(evt),
                onEnd: (evt) => this.handleSortEnd(evt),
                //onMove: (evt) => this.handleSortMove(evt),
            });
        });
    };

    handleSortBegin(evt)
    {
        this.trigger('items:sort', evt);
    };


    handleSortEnd(evt)
    {
        this.trigger('items:sorted', evt);
    };
}

customElements.define('wd-list', WDList);
